import { logManager } from '../logs'

// Helpers.
import { PEER, ICE_COLLECTION_CHECK_REASONS, ICE_COLLECTION_RESULT_TYPES } from '../constants'

// SDP Helpers.
import sdpPipeline from '../sdpUtils/pipeline'
import { isUnifiedPlan } from '../sdpUtils/sdpSemantics'
import { getSdpSessionId } from '../sdpUtils/extractors'

// General Utils.
import { getBrowserDetails, mergeValues } from '../utils'
import iceCollectionScheduledCheck from '../Peer/utils/iceCollectionScheduledCheck'

// Libraries.
import EventEmitter from 'eventemitter3'
import { debounce } from 'lodash/fp'
const unfixedDebounce = debounce.convert({ fixed: false })

/**
 * Object to represent a webRTC Session for a single peer.
 * "Session" being an abstraction of a webRTC connection between another peer.
 * Performs logic for initializing and connecting a peer connection.
 * Manages media added to / received from the peer connection.
 * @method Session
 */
export default function Session (id, managers, config = {}) {
  const log = logManager.getLogger('Session', id)
  log.info('Creating new Session.')
  const sdpSemantics = config.peer && config.peer.rtcConfig && config.peer.rtcConfig.sdpSemantics
  log.debug(`Session configured for ${sdpSemantics || 'default'} SDP semantics.`)

  // Internal variables.
  const sessionId = id
  let peerId
  const peerManager = managers.peerManager
  const mediaManager = managers.mediaManager
  const trackManager = managers.trackManager
  const emitter = new EventEmitter()

  // Session settings
  const settings = {
    dscpControls: {}
  }

  // This structure is used to remember the dtls role of a peer relative to some remote sdp.
  // key: the remote sdp's session id (o= line).
  // value: the dtls role that the current peer's local sdp has.
  // This is needed because a remote SDP with the same sessionId as a previous one may reappear later on
  //  (music-unhold scenario) and the roles will be expected to be the same.
  const dtlsRoleRecord = new Map()

  // The latest remote description successfully set, even if the Peer has
  //    been recreated since it was set.
  let latestRemoteDescription

  // Create and initialize the peer
  let peer = peerManager.create(config.peer)
  if (peer) {
    // The id of the created peer
    peerId = peer.id
    setupPeerEventHandlers(peer)
  } else {
    throw new Error(`Peer creation error in Session ${sessionId}.`)
  }

  /**
   * Records the peer's dtlsRole if it hasn't been recorded yet and associates it with a remote sdp's sessionId.
   * @method recordNewDtlsRole
   */
  function recordNewDtlsRole () {
    const remoteSdpSessionId = getSdpSessionId(peer.remoteDescription.sdp)
    if (!dtlsRoleRecord.get(remoteSdpSessionId)) {
      dtlsRoleRecord.set(remoteSdpSessionId, peer.dtlsRole)
    }
  }

  /**
   * @property {Array} getLocalTracks List of active Track objects the Session has added locally.
   */
  function getLocalTracks () {
    const peer = peerManager.get(peerId)
    if (peer) {
      return peer.localTracks
    } else {
      return []
    }
  }

  /**
   * @property {Array} getAllLocalTracks List of all Track objects the Session has added locally.
   */
  function getAllLocalTracks () {
    const peer = peerManager.get(peerId)
    if (peer) {
      // Get all local Track objects, not just the active ones.
      return peer.senderTracks.map(nativeTrack => trackManager.get(nativeTrack.id))
    } else {
      return []
    }
  }

  /**
   * @property {Array} getRemoteTracks List of active Track objects the Session has received remotely.
   */
  function getRemoteTracks () {
    const peer = peerManager.get(peerId)
    if (peer) {
      return peer.remoteTracks
    } else {
      return []
    }
  }

  /**
   * Retrieve a snapshot of the Session object's current state.
   * @method getState
   * @return {Object}
   */
  function getState () {
    return {
      id: sessionId,
      localTracks: getLocalTracks(),
      remoteTracks: getRemoteTracks(),
      allLocalTracks: getAllLocalTracks()
    }
  }

  /**
   * Setup a warm PeerConnection.
   * @method warmup
   */
  function warmup () {}

  /**
   * Add Track objects to the Session.
   * @method addTracks
   * @param  {Array} tracks List of Track objects.
   * @param  {Object} dscpTrackMapping Track - DSCPSettings map
   */
  function addTracks (tracks, dscpTrackMapping) {
    const peer = peerManager.get(peerId)
    // TODO: Better error handling?
    if (peer) {
      const addTrackOrReuseTransceiverPromises = tracks.map(track => {
        return new Promise((resolve, reject) => {
          // We try to find a reusable transceiver that we can attach the track to achieve the following:
          // - Avoid transceiver pollution and needing to create a brand new transceiver to attach the track to.
          // - Allow re-adding of the same track type that has been previously removed.
          //   (This is so that we can still have re-adding of tracks when using the "basic" media API which imposes a 1-audio & 1-video limit)
          const reusableTransceiver = peer.findReusableTransceiver(track.track.kind)

          // If we can find a reusable transceiver, reuse it.
          if (isUnifiedPlan(config.peer.rtcConfig.sdpSemantics) && reusableTransceiver) {
            reusableTransceiver.sender
              // Replace the dummy track on the Sender with the actual track we want to send.
              .replaceTrack(track.track)
              .then(() => {
                /*
                 * Set the correct direction on the Transceiver to include that we now want to send:
                 *   - sendrecv --> sendrecv
                 *   - sendonly --> sendonly
                 *   - recvonly --> sendrecv
                 *   - inactive --> sendonly
                 */
                reusableTransceiver.direction = ['sendrecv', 'recvonly'].includes(reusableTransceiver.direction)
                  ? 'sendrecv'
                  : 'sendonly'

                resolve(
                  `Track (${track.track.kind} : ${track.id}) reused transceiver (mid: ${reusableTransceiver.mid}).`
                )
              })
              .catch(err => {
                log.error(err)
                reject(err)
              })
          } else {
            // To get around the current limitation described above, we use peerConnection's `addTrack` when we can't find a reusable transceiver.
            // `addTrack` does one of the following when called:
            // - Create a new transceiver and attaches the track and stream to the sender
            // - Find and use an existing transceiver that has never been used to send data before and attach the track and stream to the sender.
            peer.addTrack(track)
            resolve(`Added track (${track.track.kind} : ${track.id}).`)
          }
        }).then(message => {
          // Set event emitters and handlers
          log.info(message)

          // Indicate that the Session has a new Track.
          emitter.emit('new:track', {
            local: true,
            trackId: track.id
          })

          settings.dscpControls = mergeValues(settings.dscpControls, dscpTrackMapping)

          track.once('ended', ({ isUnsolicited }) => {
            const peer = peerManager.get(peerId)
            if (peer) {
              // If the PeerConnection is closed, we don't need to worry about
              //    removing the track (and it would throw an error anyway).
              if (peer.signalingState !== 'closed') {
                // If this track ending was expected, remove it from the Peer
                //    immediately. Otherwise another operation will remove it.
                if (!isUnsolicited) {
                  peer.removeTrack(track.id)
                }

                emitter.emit('track:ended', {
                  local: true,
                  trackId: track.id,
                  isUnsolicited
                })
                // Remove track from session dscp settings
                if (settings.dscpControls.hasOwnProperty(track.id)) {
                  log.debug(`Removing track ${track.id} from session dscp settings`)
                  delete settings.dscpControls[track.id]
                }
              } else {
                log.debug(
                  `Received ended event for track ${track.id}, but its associated Peer ${peer.id} is closed. Ignoring this event...`
                )
              }
            }
          })
        })
      })

      return Promise.all(addTrackOrReuseTransceiverPromises)
    }
  }

  /**
   * Creates a local SDP offer.
   * @method createOffer
   * @param  {Object} [options] Options for configuring the SDP.
   * @param  {Object} [options.mediaDirections] Directions to use for media.
   * @param  {Array}  [options.sdpHandlers] SDP handlers for modifying the local offer.
   * @return {Promise} Resolves with the offer.
   */
  function createOffer (options = {}) {
    return new Promise((resolve, reject) => {
      const peer = peerManager.get(peerId)
      if (!peer) {
        reject(new Error(`Peer not found in Session ${sessionId}.`))
      }

      // If using unified-plan, remove options.mediaDirections.
      // This is because directions are now set in transceivers.
      if (isUnifiedPlan(config.peer.rtcConfig.sdpSemantics)) {
        if (options.mediaDirections) {
          setLocalAndRemoteTransceiversDirection(options.mediaDirections)
          delete options.mediaDirections
        }
      }

      peer
        .createOffer(options)
        .then(offer => {
          if (options.sdpHandlers || sdpPipeline.getHandlers().length) {
            log.debug('Modifying local offer with SDP pipeline.')
            offer.sdp = sdpPipeline.run(options.sdpHandlers, offer.sdp, {
              type: offer.type,
              endpoint: PEER.ENDPOINT.LOCAL
            })
          }

          resolve(offer)
        })
        .catch(reject)
    })
  }

  /**
   * Sets a local SDP.
   * @method setLocalDescription
   * @param  {Object} description The description containing the SDP to set as the local description.
   * @return {Promise} Resolves with the description.
   */
  function setLocalDescription (description) {
    return new Promise((resolve, reject) => {
      const peer = peerManager.get(peerId)
      if (!peer) {
        reject(new Error(`Peer not found in Session ${sessionId}.`))
      }

      peer
        .setLocalDescription(description)
        .then(() => {
          // Record the peer's dtls role if setting a local answer.
          if (description.type === 'answer') {
            recordNewDtlsRole()
          }

          // Set any parameters on the peer's senders if applicable
          setParameters()

          // Resolve with the _current_ local description, which may be
          //    different than the provided description due trickle ICE config.
          resolve(peer.localDescription)
        })
        .catch(reject)
    })
  }

  /**
   * Rollback the local description.
   * @method rollbackLocalDescription
   * @return {Promise} Resolves with the rollbacked description or error.
   */
  function rollbackLocalDescription () {
    return new Promise((resolve, reject) => {
      const peer = peerManager.get(peerId)
      if (!peer) {
        reject(new Error(`Peer not found in Session ${sessionId}.`))
      }

      peer
        .setLocalDescription({ type: 'rollback' })
        .then(() => {
          resolve(peer.localDescription)
        })
        .catch(reject)
    })
  }

  /**
   * Parse the result of a call to the ice collection check function and act accordingly:
   *  'StartCall' type - emit the negotiation ready event
   *  'Error' type - end the session
   *  'Wait' type - Schedule a callback to the ice collection check function
   * @method iceCollectionCheckResult
   * @param {Object} result The result of ice collection check function.
   * @param {string} result.type The action to take.
   * @param {number} result.wait How many ms to wait for the next scheduled check.
   * @return {Promise} Resolves with the rollbacked description or error.
   */
  function iceCollectionCheckResult (result) {
    const peer = peerManager.get(peerId)
    if (!peer) {
      return new Error(`Peer not found in Session ${sessionId}.`)
    }

    if (result.type === ICE_COLLECTION_RESULT_TYPES.START_CALL) {
      log.info('Proceeding with the negotiation as instructed by the ice collection check.')
      peer.isIceCollectionCheckOngoing = false
      peer.emitter.emit('onnegotiationready')
    } else if (result.type === ICE_COLLECTION_RESULT_TYPES.ERROR) {
      log.info('Cleaning up session as instructed by the ice collection check.')
      peer.isIceCollectionCheckOngoing = false
      const localTracks = getLocalTracks()
      localTracks.forEach(track => track.stop())
      end()
    } else if (result.type === ICE_COLLECTION_RESULT_TYPES.WAIT) {
      // If `result.wait` is not set, then the ice collection check function is only waiting for either
      //  ice candidate events or ice gathering state change events
      if (result.wait) {
        log.debug(`Setting a scheduled check for the ice collection check after ${result.wait} ms`)
        peer.iceLoop = setTimeout(() => iceCollectionScheduledCheck(peer), result.wait)
      }
    } else {
      log.info('Unexpected result type from ICE collection check function, ending call.')
      peer.isIceCollectionCheckOngoing = false
      const localTracks = getLocalTracks()
      localTracks.forEach(track => track.stop())
      end()
    }
  }

  /**
   * Sets parameters on the RTCRtpSenders
   * @param {Object} mediaConstraints
   */
  function setParameters (mediaConstraints) {
    // RTCRtpSender.setParameters is currently unsupported on Firefox (or at least not supported as it should be)
    if (getBrowserDetails().browser === 'firefox') {
      log.debug('Setting sender parameters not supported on Firefox; skipping.')
      return
    }

    const promises = []

    // Set the DSCPControls (i.e., network priorities) if set in the session's settings
    for (const trackId in settings.dscpControls) {
      promises.push(
        new Promise((resolve, reject) => {
          log.debug(`Setting networkPriority ${settings.dscpControls[trackId]} for sender with track ${trackId}`)
          setNetworkPriority(trackId, settings.dscpControls[trackId])
            .then(resolve)
            .catch(reject)
        })
      )
    }

    return Promise.all(promises)
  }

  /**
   * Sets the networkPriority in the peer connection's sender's encodings.
   * @method setNetworkPriority
   * @param {string} trackId The track ID
   * @param {string} networkPriority The network priority value to set
   * @return {Promise} Resolves with the setParameters()
   */
  function setNetworkPriority (trackId, networkPriority) {
    const peer = peerManager.get(peerId)
    const senders = peer.getSenders()

    // search for a sender associated with the provided track id
    const sender = senders.find(sender => sender.track.id === trackId)
    if (sender) {
      // Need to perform a getParameters() prior to a setParameters()
      const parameters = sender.getParameters()
      parameters.encodings.forEach(encoding => {
        encoding.networkPriority = networkPriority
      })
      return sender.setParameters(parameters)
    } else {
      const errMsg = `Cannot find sender associated with trackId: ${trackId}`
      log.info(errMsg)
      return Promise.reject(errMsg)
    }
  }

  /**
   * Generates (and sets) a local SDP offer.
   * @method generateOffer
   * @param  {Object} [options] Options for configuring the SDP.
   * @param  {Object} [options.mediaDirections] Directions to use for media.
   * @param  {Array}  [options.sdpHandlers] SDP handlers for modifying the local offer.
   * @return {Promise} Resolves with the offer.
   */
  function generateOffer (options = {}) {
    // Backwards compatibility: Create the offer and set it as one operation.
    return new Promise((resolve, reject) => {
      createOffer(options)
        .then(setLocalDescription)
        .then(resolve)
        .catch(reject)
    })
  }

  /**
   * Replaces a specified transceiver's sender.track.
   * @method replaceTrack
   * @param {Object} newTrack The MediaStreamTrack we want to place into the sender.
   * @param {Object} options Options for specifying which transceiver's sender should be replaced. They are ordered by priority.
   * @param {String} [options.trackId] The transceiver with the specific sender.track.id.
   * @param {String} [options.mid] The transceiver with the specific media id.
   * @return {Object} A Promise object which is fulfilled once the track has been replaced
   */
  function replaceTrack (newTrack, options) {
    const peer = peerManager.get(peerId)
    const track = trackManager.get(newTrack.id)
    return peer
      .replaceTrack(track.track, options)
      .then(() => {
        track.once('ended', ({ isUnsolicited }) => {
          const peer = peerManager.get(peerId)
          if (peer) {
            // If the PeerConnection is closed, we don't need to worry about
            //    removing the track (and it would throw an error anyway).
            if (peer.signalingState !== 'closed') {
              // If this track ending was expected, remove it from the Peer
              //    immediately. Otherwise another operation will remove it.
              if (!isUnsolicited) {
                peer.removeTrack(track.id)
              }

              emitter.emit('track:ended', {
                local: true,
                trackId: track.id,
                isUnsolicited
              })
              // Remove track from session dscp settings
              if (settings.dscpControls.hasOwnProperty(track.id)) {
                log.debug(`Removing track ${track.id} from session dscp settings`)
                delete settings.dscpControls[track.id]
              }
            } else {
              log.debug(
                `Received ended event for track ${track.id}, but its associated Peer ${peer.id} is closed. Ignoring this event...`
              )
            }
          }
        })

        emitter.emit('track:replaced', {
          oldTrackId: options.trackId,
          trackId: newTrack.id
        })
      })
      .catch(err => err)
  }

  /**
   * Sets the direction of transceivers.
   * @method setTransceiversDirection
   * @param {String} targetDirection The desired direction to set the transceivers to.
   * @param {Object} [options] Options for specifying which transceivers should be affected.
   *  trackIds option has priority
   * @param {Array} [options.trackIds] The optional list of track ids whose transceivers we want to set the direction of.
   * @return {Object} An object containing an `error` flag and  an array `failures` of transceiver "mid"s whose directions weren't changed.
   */
  function setTransceiversDirection (targetDirection, options = {}) {
    if (isUnifiedPlan(config.peer.rtcConfig.sdpSemantics)) {
      const peer = peerManager.get(peerId)
      return peer.setTransceiversDirection(targetDirection, options)
    } else {
      log.info('Transceiver direction modification is only available in unified-plan.')
      return { error: true }
    }
  }

  /**
   * Sets the direction of transceivers by checking both the local and remote track IDs
   * @method setLocalAndRemoteTransceiversDirection
   * @param {Object} mediaDirections Options for specifying the directions we want to set for certain medias (i.e., audio and video)
   * @param {Array} [mediaDirections.audio] The direction we want to set of the transceivers with audio tracks
   * @param {Array} [mediaDirections.video] The direction we want to set of the transceivers with video tracks
   * @return {Object} An object containing an `error` flag and  an array `failures` of transceiver "mid"s whose directions weren't changed.
   */
  function setLocalAndRemoteTransceiversDirection (mediaDirections) {
    const peer = peerManager.get(peerId)

    if (mediaDirections.audio) {
      const localAudioTrackIds = peer.localTracks.filter(track => track.track.kind === 'audio').map(track => track.id)
      const remoteAudioTrackIds = peer.remoteTracksAll
        .filter(track => track.track.kind === 'audio')
        .map(track => track.id)

      const audioResult = peer.setTransceiversDirection(mediaDirections.audio, {
        trackIds: [...localAudioTrackIds, ...remoteAudioTrackIds]
      })
      if (audioResult.error) {
        log.info(`Failed to process the following transceivers: ${audioResult.failures}`)
      }
    }

    if (mediaDirections.video) {
      const localVideoTrackIds = peer.localTracks.filter(track => track.track.kind === 'video').map(track => track.id)
      const remoteVideoTrackIds = peer.remoteTracksAll
        .filter(track => track.track.kind === 'video')
        .map(track => track.id)

      const videoResult = peer.setTransceiversDirection(mediaDirections.video, {
        trackIds: [...localVideoTrackIds, ...remoteVideoTrackIds]
      })
      if (videoResult.error) {
        log.info(`Failed to process the following transceivers: ${videoResult.failures}`)
      }
    }
  }

  /**
   * Processes (and sets) a remote SDP offer.
   * @method processOffer
   * @param  {RTCSessionDescription} offer
   * @param  {Object} [options] Options for configuring the SDP.
   * @param  {Array}  [options.sdpHandlers] SDP handlers for modifying the remote offer.
   * @return {Promise}
   */
  function processOffer (offer, options = {}) {
    return new Promise((resolve, reject) => {
      const peer = peerManager.get(peerId)
      if (!peer) {
        reject(new Error(`Peer not found in Session ${sessionId}.`))
      }

      if (options.sdpHandlers || sdpPipeline.getHandlers().length) {
        log.debug('Modifying remote offer with SDP pipeline.')
        offer.sdp = sdpPipeline.run(options.sdpHandlers, offer.sdp, {
          type: offer.type,
          endpoint: PEER.ENDPOINT.REMOTE
        })
      }

      peer
        .setRemoteDescription(offer)
        .then(() => {
          // Set the offer as the latest remote description.
          latestRemoteDescription = offer
          resolve()
        })
        .catch(reject)
    })
  }

  /**
   * Creates a local SDP answer.
   * @method createAnswer
   * @param  {Object} [options] Options for configuring the SDP.
   * @param  {Object} [options.mediaDirections] Directions to use for media.
   * @param  {Array}  [options.sdpHandlers] SDP handlers for modifying the local answer.
   * @return {Promise} Resolves with the answer.
   */
  function createAnswer (options = {}) {
    return new Promise((resolve, reject) => {
      const peer = peerManager.get(peerId)
      if (!peer) {
        reject(new Error(`Peer not found in Session ${sessionId}.`))
      }

      // If using unified-plan, remove options.mediaDirections.
      // This is because directions are now set in transceivers.
      if (isUnifiedPlan(config.peer.rtcConfig.sdpSemantics)) {
        if (options.mediaDirections) {
          setLocalAndRemoteTransceiversDirection(options.mediaDirections)
          delete options.mediaDirections
        }
      }

      // Set the dtlsRole here if the following are true:
      // - It previously existed for this specific remote sdp.
      // - It hasn't been set on a recreated peer yet.
      const remoteSdpSessionId = getSdpSessionId(peer.remoteDescription.sdp)
      const previousDtlsRole = dtlsRoleRecord.get(remoteSdpSessionId)
      if (!peer.dtlsRole && previousDtlsRole) {
        peer.dtlsRole = previousDtlsRole
      }

      peer
        .createAnswer(options)
        .then(answer => {
          if (options.sdpHandlers || sdpPipeline.getHandlers().length) {
            log.debug('Modifying local answer with SDP pipeline.')
            answer.sdp = sdpPipeline.run(options.sdpHandlers, answer.sdp, {
              type: answer.type,
              endpoint: PEER.ENDPOINT.LOCAL
            })
          }
          resolve(answer)
        })
        .catch(reject)
    })
  }

  /**
   * Generates (and sets) a local SDP answer.
   * @method generateAnswer
   * @param  {Object} [options] Options for configuring the SDP.
   * @param  {Object} [options.mediaDirections] Directions to use for media.
   * @param  {Array}  [options.sdpHandlers] SDP handlers for modifying the local answer.
   * @return {Promise} Resolves with the answer.
   */
  function generateAnswer (options = {}) {
    // For backwards compatibility: Should use other APIs directly.
    return new Promise((resolve, reject) => {
      createAnswer(options)
        .then(setLocalDescription)
        .then(resolve)
        .catch(reject)
    })
  }

  /**
   * Processes (and sets) a remote SDP answer.
   * @method processAnswer
   * @param  {RTCSessionDescription} answer
   * @param  {Object} [options] Options for configuring the SDP.
   * @param  {Array}  [options.sdpHandlers] SDP handlers for modifying the remote answer.
   * @return {Promise}
   */
  function processAnswer (answer, options = {}) {
    if (options.sdpHandlers || sdpPipeline.getHandlers().length) {
      log.debug('Modifying remote answer with SDP pipeline.')
      answer.sdp = sdpPipeline.run(options.sdpHandlers, answer.sdp, {
        type: answer.type,
        endpoint: PEER.ENDPOINT.REMOTE
      })
    }

    return new Promise((resolve, reject) => {
      const peer = peerManager.get(peerId)
      if (!peer) {
        reject(new Error(`Peer not found in Session ${sessionId}.`))
      }

      peer
        .setRemoteDescription(answer)
        .then(() => {
          // Record the peer's dtls role.
          recordNewDtlsRole()

          // Set the answer as the latest remote description.
          latestRemoteDescription = answer

          resolve()
        })
        .catch(reject)
    })
  }

  /**
   * Add an ICE candidate from the remote endpoint.
   * @method addIceCandidate
   * @param  {RTCIceCandidate} candidate
   * @param  {Object} [options]
   * @param  {string} [options.label] The Peer to perform the operation on.
   * @return {Promise}
   */
  function addIceCandidate (candidate, options = {}) {
    return new Promise((resolve, reject) => {
      const peer = peerManager.get(peerId)
      if (!peer) {
        reject(new Error(`Peer not found in Session ${sessionId}.`))
      }

      peer
        .addIceCandidate(candidate)
        .then(resolve)
        .catch(reject)
    })
  }

  /**
   * End the Session.
   * @method end
   */
  function end () {
    log.info('Ending Session.')
    const peer = peerManager.get(peerId)
    if (peer) {
      peer.close()
    }
    emitter.emit('session:ended', sessionId)
  }

  /**
   * Removes specified Track objects from the Session.
   * @method removeTrack
   * @param  {Array} trackIds List of IDs of Track objects to remove.
   */
  function removeTracks (trackIds) {
    const peer = peerManager.get(peerId)
    if (peer) {
      // Get the list of all tracks on the Peer (event ended ones).
      const allLocalTracks = peer.senderTracks
      trackIds.forEach(trackId => {
        if (allLocalTracks.findIndex(track => track.id === trackId) > -1) {
          peer.removeTrack(trackId)
          // Remove the track from the session dscp settings
          if (settings.dscpControls.hasOwnProperty(trackId)) {
            log.debug(`Removing track ${trackId} from session dscp settings`)
            delete settings.dscpControls[trackId]
          }
        }
      })
    }
  }

  /*
   * Restarts ICE connection
   * @method restartIce
   */
  function restartIce () {
    const peer = peerManager.get(peerId)
    if (peer) {
      peer.restartIce()
    }
  }

  /*
   * Get the DTLS role of the Peer
   * @method getDtlsRole
   * @return {string} The DTLS role of the Peer
   */
  function getDtlsRole () {
    const peer = peerManager.get(peerId)
    if (peer) {
      return peer.dtlsRole
    }
  }

  /**
   * Send DTMF tones on specified Track object from the Session.
   * @method sendDTMF
   * @param {Object} options The DTMF options.
   * @param {string} options.tone DTMF tone to send. Valid values are [0,1,2,3,4,5,6,7,8,9,#].
   * @param {number} [options.duration=100] The amount of time, in milliseconds, that each DTMF tone should last.
   * @param {number} [options.intertoneGap=70] The length of time, in milliseconds, to wait between tones.
   * @param {Object} [sendOptions] The send options.
   * @param {func} [sendOptions.callback] Optional callback for tone event .
   * @param {string} [sendOptions.trackId] The trackId of the sender to use.
   * @return {boolean} The success or fail indicator
   */
  function sendDTMF (DTMFOptions, sendOptions = {}) {
    const peer = peerManager.get(peerId)
    if (peer) {
      return peer.sendDTMF(DTMFOptions, sendOptions)
    } else {
      return false
    }
  }

  /**
   * Retrieve RTCStatsReport from a sender.
   * @method getStats
   * @param  {string} trackId The track id associated with a sender.
   * @return {Promise}
   */
  function getStats (trackId) {
    return new Promise((resolve, reject) => {
      const peer = peerManager.get(peerId)
      if (!peer) {
        reject(new Error(`Peer not found in Session ${sessionId}.`))
        return
      }
      peer
        .getStats(trackId)
        .then(resolve)
        .catch(reject)
    })
  }

  function on (...args) {
    return emitter.on(...args)
  }

  function once (...args) {
    return emitter.once(...args)
  }

  function off (...args) {
    return emitter.off(...args)
  }

  /**
   * Sets up event handlers on a given peer.
   * @method setupPeerEventHandlers
   * @param {PeerConnection} targetPeer The peer to set event handlers on.
   */
  function setupPeerEventHandlers (targetPeer) {
    targetPeer.oniceconnectionstatechange = event => {
      emitter.emit('peer:iceConnectionStateChange', {
        iceConnectionState: targetPeer.iceConnectionState
      })
    }

    // TODO: Use `uniqueLabel` when setting event listeners (and bubbling events).
    // When the peer gets an ICE candidate, emit it as
    //  a message to be sent to the other end.
    targetPeer.onicecandidate = event => {
      emitter.emit('onicecandidate', {
        candidate: event.candidate
      })
    }

    /*
     * Ice Collection Check Events:
     *
     * Emit an event for the session channel everytime:
     *  - the ICE gathering state changes
     *  - an ICE candidate is collected
     *  - it is time for a scheduled call to the collection check function
     *
     */
    targetPeer.onicegatheringstatechange = event => {
      // Only emit the event if we are in an ongoing ice collection check loop
      if (targetPeer.isIceCollectionCheckOngoing) {
        // Clear any previous timeout/loop
        if (targetPeer.iceLoop) {
          clearTimeout(targetPeer.iceLoop)
          targetPeer.iceLoop = undefined
        }
        emitter.emit('peer:iceGatheringStateChange', {
          reason: ICE_COLLECTION_CHECK_REASONS.ICE_GATHERING_STATE_CHANGE,
          iceCollectionDuration: targetPeer.iceTimer.timeFromStart(),
          iceCandidates: targetPeer.iceCandidates,
          iceGatheringState: targetPeer.iceGatheringState,
          rtcPeerConnectionConfig: targetPeer.config,
          rtcLocalSessionDescription: targetPeer.localDescription
        })
      }
    }

    // Debounce the listener for the ice candidate event since they can be emitted quite quickly
    //  one after another. Options: leading:true will ensure the first invocation always occur, and
    //  trailing:false (default) ensures the last invocation will occur after the delay as appropriate.
    targetPeer.on(
      'iceCandidateCollected',
      unfixedDebounce(50)(
        iceCollectionInfo => {
          // Only emit the event if we are in an ongoing ice collection check process
          if (targetPeer.isIceCollectionCheckOngoing) {
            // Clear any previous timeout/loop
            if (targetPeer.iceLoop) {
              clearTimeout(targetPeer.iceLoop)
              targetPeer.iceLoop = undefined
            }
            emitter.emit('peer:iceCandidateCollected', {
              reason: ICE_COLLECTION_CHECK_REASONS.NEW_CANDIDATE,
              ...iceCollectionInfo
            })
          }
        },
        { leading: true }
      )
    )

    targetPeer.on('scheduledCheck', iceCollectionInfo => {
      // Only emit the event if we are in an ongoing ice collection check loop
      if (targetPeer.isIceCollectionCheckOngoing) {
        // Clear any previous timeout/loop
        if (targetPeer.iceLoop) {
          clearTimeout(targetPeer.iceLoop)
          targetPeer.iceLoop = undefined
        }
        emitter.emit('peer:iceCollectionScheduledCheck', {
          reason: ICE_COLLECTION_CHECK_REASONS.SCHEDULED,
          ...iceCollectionInfo
        })
      }
    })

    // Handle when the Peer receives a new remote track.
    targetPeer.ontrack = track => {
      let media = mediaManager.get(track.getStream().id)
      if (media) {
        // Add the new Track to its Media object.
        media.addTrack(track)
      } else {
        // Create a new Media object using the Track.
        media = mediaManager.createRemote(track.getStream(), [track])
      }

      track.once('ended', () => {
        emitter.emit('track:ended', {
          local: false,
          trackId: track.id,
          // If a remote track is ended, then a remote action triggered it. Consider
          //    this solicited since we don't want to take an action here.
          isUnsolicited: false
        })
      })

      const { kind } = track.getState()
      log.info(`Received new track (${kind} : ${track.id})`)

      // Indicate that the Session has a new Track.
      emitter.emit('new:track', {
        local: false,
        trackId: track.id
      })
    }
  }

  /**
   * Creates a new peer and replaces the current peer being used by this session.
   *     This will close the previous Peer, stopping any media being sent/received on it.
   * @method recreatePeer
   */
  function recreatePeer () {
    const oldPeer = peer
    const newPeer = peerManager.create(config.peer)
    if (newPeer) {
      // The id of the created peer
      peerId = newPeer.id
      log.debug(`Recreated Peer with ID: ${peerId}`)
      peer = newPeer

      // Copy tracks
      oldPeer.localTracks.forEach(oldLocalTrack => {
        newPeer.addTrack(oldLocalTrack)
      })

      // Set event handlers
      setupPeerEventHandlers(newPeer)

      // Close the old peer
      oldPeer.close()
    } else {
      throw new Error(`Peer creation error in Session ${sessionId}.`)
    }
  }

  function getPeer () {
    return peer
  }

  function getRemoteDescription () {
    return peer.remoteDescription
  }

  function getLatestRemoteDesc () {
    return latestRemoteDescription
  }

  /**
   * Given a list of media section IDs, return the list of remote track IDs from
   *     the Transceivers that the media sections represent.
   * @param {Array<string>} mids List of media section IDs.
   * @return {Array<string>} List of remote track IDs.
   */
  function getRemoteTrackIdsFromTransceivers (mids) {
    const transceivers = peer.getTransceivers()

    const remoteTrackIds = []
    // Iterate over every transceiver
    transceivers.forEach(transceiver => {
      // If the transceiver's mid value is in our list of mids, add it to the
      //    remoteTrackIds array to be returned.
      // The `if` condition looks sketchy because we are comparing a string to
      //    a number.
      if (mids.some(mid => mid + '' === transceiver.mid)) {
        const id = transceiver.receiver.track.id
        remoteTrackIds.push(id)
      }
    })

    return remoteTrackIds
  }

  /**
   * Get all active, incoming remote tracks that are part of the Session.
   * @return {Array<string>} List of remote track IDs.
   */
  function getIncomingRemoteTrackIds () {
    const transceivers = peer.getTransceivers()

    const remoteTrackIds = []
    transceivers.forEach(transceiver => {
      // Check the `direction` of the transceiver.
      // For incoming calls, the `currentDirection` property will not be set since that transceiver
      // is not yet fully negotiated.
      const isReceiving =
        transceiver.mid !== null &&
        transceiver.direction.includes('recv') &&
        transceiver.receiver.track.readyState === 'live'
      if (isReceiving) {
        remoteTrackIds.push(transceiver.receiver.track.id)
      }
    })

    return remoteTrackIds
  }

  /**
   * Get all active, remote tracks that are part of the Session.
   * @return {Array<string>} List of remote track IDs.
   */
  function getActiveRemoteTrackIds () {
    const transceivers = peer.getTransceivers()

    const remoteTrackIds = []
    transceivers.forEach(transceiver => {
      // transceiver.mid !== null --> The Transceiver is in the SDP.
      // currentDirection --> The Transceiver has been negotiated.
      // currentDirection.includes(recv) --> It has a remote track.
      // track.readyState === live ---> It is active (...not thorough).
      const isReceiving =
        transceiver.mid !== null &&
        transceiver.currentDirection &&
        transceiver.currentDirection.includes('recv') &&
        transceiver.receiver.track.readyState === 'live'
      if (isReceiving) {
        remoteTrackIds.push(transceiver.receiver.track.id)
      }
    })

    return remoteTrackIds
  }

  /**
   * The exposed API.
   */
  return {
    id: sessionId,
    config,
    // Getter APIs.
    get peer () {
      return getPeer()
    },
    getState,
    get localTracks () {
      return getLocalTracks()
    },
    get remoteTracks () {
      return getRemoteTracks()
    },
    get allLocalTracks () {
      return getAllLocalTracks()
    },
    warmup,
    addTracks,
    removeTracks,
    replaceTrack,
    setTransceiversDirection,
    setLocalAndRemoteTransceiversDirection,
    // Negotiation APIs.
    createOffer,
    createAnswer,
    setLocalDescription,
    getRemoteDescription,
    getLatestRemoteDesc,
    generateOffer,
    processOffer,
    generateAnswer,
    processAnswer,
    iceCollectionCheckResult,
    // Other APIs.
    recreatePeer,
    rollbackLocalDescription,
    addIceCandidate,
    restartIce,
    getDtlsRole,
    end,
    sendDTMF,
    getStats,
    setParameters,
    getRemoteTrackIdsFromTransceivers,
    getIncomingRemoteTrackIds,
    getActiveRemoteTrackIds,
    // Event APIs.
    on,
    once,
    off
  }
}
